import React from 'react'
import { Box, Avatar, Typography } from '@mui/material'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import MicOffIcon from '@mui/icons-material/MicOff'
import MicIcon from '@mui/icons-material/Mic'
import PersonIcon from '@mui/icons-material/Person'
import { mediaURL } from 'src/consumer/services'

/**
 * Parse RTM username that's in JSON format
 * Format: { role, name, profilePicId }
 */
const parseRtmUsername = (rtmUsername) => {
  try {
    if (!rtmUsername) {
      return { role: null, name: 'Unknown', profilePicId: null }
    }
    
    if (typeof rtmUsername === 'string') {
      try {
        const parsed = JSON.parse(rtmUsername)
        return {
          role: parsed.role || null,
          name: parsed.name || 'Unknown',
          profilePicId: parsed.profilePicId || null,
        }
      } catch (e) {
        // If not JSON, use as name directly
        return { role: null, name: rtmUsername, profilePicId: null }
      }
    }
    
    return { role: null, name: 'Unknown', profilePicId: null }
  } catch (error) {
    return { role: null, name: 'Unknown', profilePicId: null }
  }
}

/**
 * Check if string is a valid HTTP URL
 */
const isValidHttpUrl = (string) => {
  try {
    const url = new URL(string)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (_) {
    return false
  }
}

/**
 * Custom Video Placeholder component for when camera is turned off
 * Shows a beautiful avatar with gradient background
 */
const CustomVideoPlaceholder = ({ user, isShown, showButtons, showSwap, uidToUsernameMap, localRtmUsername }) => {
  // Check if user has audio (mic is on)
  const hasAudio = user?.hasAudio === 1 || user?.hasAudio === true
  
  // Get parsed user info from RTM username (handles missing fields gracefully)
  const getUserInfo = () => {
    try {
      // Get username from mapping
      let rawUsername = null
      if (uidToUsernameMap && user?.uid !== undefined) {
        const uid = user.uid
        const uidNum = typeof uid === 'string' ? parseInt(uid, 10) : Number(uid)
        const uidStr = String(uid)
        
        // Try all possible key formats
        rawUsername = uidToUsernameMap[uid] || 
                      uidToUsernameMap[uidNum] || 
                      uidToUsernameMap[uidStr] ||
                      uidToUsernameMap[String(uidNum)] ||
                      uidToUsernameMap[Number(uidStr)]
        
        // If still not found, iterate through all map keys
        if (!rawUsername && Object.keys(uidToUsernameMap).length > 0) {
          for (const [key, value] of Object.entries(uidToUsernameMap)) {
            const keyStr = String(key)
            const uidStrForCompare = String(uid)
            const keyNum = Number(key)
            const uidNumForCompare = Number(uid)
            
            if (keyStr === uidStrForCompare || 
                (!isNaN(keyNum) && !isNaN(uidNumForCompare) && keyNum === uidNumForCompare)) {
              rawUsername = value
              break
            }
          }
        }
      }
      
      // For local user (uid === 0), if not found in map, use localRtmUsername as fallback
      if (!rawUsername && user?.uid === 0 && localRtmUsername) {
        rawUsername = localRtmUsername
      }
      
      // Check if RTM data has been received
      const hasRtmData = !!rawUsername
      
      // Parse with safe defaults
      const parsed = parseRtmUsername(rawUsername)
      
      // For remote users with mentor role data, clear profilePicId to prevent showing wrong picture
      let profilePicId = parsed.profilePicId || null
      if (user?.uid !== 0 && parsed.role === 'mentor') {
        profilePicId = null
      }
      
      // Determine display name
      let displayName
      if (user?.uid === 0) {
        // For local user
        if (hasRtmData && parsed.name && parsed.name !== 'Unknown') {
          displayName = parsed.name
        } else if (!hasRtmData) {
          displayName = 'You'
        } else {
          displayName = 'You'
        }
      } else if (hasRtmData && parsed.name && parsed.name !== 'Unknown') {
        displayName = parsed.name
      } else if (!hasRtmData) {
        displayName = 'Loading...'
      } else {
        displayName = 'Guest'
      }
      
      return {
        role: parsed.role || null,
        name: displayName,
        profilePicId: profilePicId,
        hasRtmData,
      }
    } catch (error) {
      console.warn('Error parsing RTM username:', error)
      return {
        role: null,
        name: user?.uid === 0 ? 'You' : 'Loading...',
        profilePicId: null,
        hasRtmData: false,
      }
    }
  }
  
  const userInfo = React.useMemo(() => getUserInfo(), [user?.uid, uidToUsernameMap, localRtmUsername])
  
  // Get profile picture URL from profilePicId
  const getProfilePictureUrl = () => {
    if (!userInfo.profilePicId) return null
    
    // Check if it's already a full URL
    if (isValidHttpUrl(userInfo.profilePicId)) {
      return userInfo.profilePicId
    }
    
    // Otherwise, construct URL from mediaURL
    return `${mediaURL}${userInfo.profilePicId}`
  }
  
  const profilePictureUrl = getProfilePictureUrl()
  
  // Extract first letter of name for avatar (with safe fallback)
  const getInitials = () => {
    const name = userInfo.name
    if (name && name.length > 0 && name !== 'Loading...' && name !== 'You') {
      // Get first letter, or first letter of each word if multiple words
      const words = name.trim().split(/\s+/)
      if (words.length > 1) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase()
      }
      return name.charAt(0).toUpperCase()
    }
    // Fallback for local user if name not available
    if (user?.uid === 0) return 'Y' // "You" -> "Y"
    // For remote users without name, return null to show person icon
    return null
  }

  // Generate a consistent modern color based on uid
  const getAvatarColor = () => {
    if (user?.uid === 0) {
      // Local user - use vibrant purple theme colors
      return {
        background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 50%, #7c3aed 100%)',
        shadow: 'rgba(139, 92, 246, 0.6)',
        glow: 'rgba(167, 139, 250, 0.4)',
      }
    }
    
    // Remote users - modern vibrant gradients
    const colors = [
      { 
        bg: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #4f46e5 100%)', 
        shadow: 'rgba(139, 92, 246, 0.6)',
        glow: 'rgba(139, 92, 246, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 50%, #e11d48 100%)', 
        shadow: 'rgba(236, 72, 153, 0.6)',
        glow: 'rgba(236, 72, 153, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%)', 
        shadow: 'rgba(6, 182, 212, 0.6)',
        glow: 'rgba(6, 182, 212, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)', 
        shadow: 'rgba(16, 185, 129, 0.6)',
        glow: 'rgba(16, 185, 129, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)', 
        shadow: 'rgba(245, 158, 11, 0.6)',
        glow: 'rgba(245, 158, 11, 0.4)',
      },
      { 
        bg: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 50%, #4338ca 100%)', 
        shadow: 'rgba(99, 102, 241, 0.6)',
        glow: 'rgba(99, 102, 241, 0.4)',
      },
    ]
    
    const index = (user?.uid || 0) % colors.length
    return colors[index]
  }

  const colorScheme = getAvatarColor()

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Glass morphism background
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        // Subtle gradient overlay
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(99, 102, 241, 0.08) 50%, rgba(236, 72, 153, 0.12) 100%)',
          pointerEvents: 'none',
          zIndex: 0,
        },
        // Match video card border radius to stay within bounds
        border: 'none',
        borderRadius: 'inherit',
        overflow: 'hidden',
        // Ensure content is above overlay
        '& > *': {
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      {/* Status indicators only - positioned at bottom right */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          zIndex: 10,
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        {/* Camera status - icon only */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            backgroundColor: '#FEE2E2',
            border: '1px solid #FECACA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <VideocamOffIcon sx={{ fontSize: 12, color: '#DC2626' }} />
        </Box>
        
        {/* Mic status - icon only */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            backgroundColor: hasAudio ? '#D1FAE5' : '#FEE2E2',
            border: hasAudio ? '1px solid #A7F3D0' : '1px solid #FECACA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {hasAudio ? (
            <MicIcon sx={{ fontSize: 12, color: '#059669' }} />
          ) : (
            <MicOffIcon sx={{ fontSize: 12, color: '#DC2626' }} />
          )}
        </Box>
      </Box>

      {/* Beautiful avatar with profile picture or gradient */}
      <Box
        sx={{
          position: 'relative',
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Avatar
          src={profilePictureUrl || undefined}
          sx={{
            width: 88,
            height: 88,
            background: profilePictureUrl ? 'transparent' : colorScheme.background,
            boxShadow: profilePictureUrl 
              ? `0 8px 32px ${colorScheme.shadow}, 0 0 0 4px rgba(255, 255, 255, 0.2), 0 0 0 1px ${colorScheme.glow}` 
              : `0 8px 32px ${colorScheme.shadow}, 0 4px 16px ${colorScheme.glow}, 0 0 0 4px rgba(255, 255, 255, 0.25), inset 0 2px 4px rgba(255, 255, 255, 0.3)`,
            fontSize: '36px',
            fontWeight: 700,
            border: '0',
            backdropFilter: 'blur(12px)',
            animation: 'gentle-pulse 4s ease-in-out infinite, subtle-rotate 20s linear infinite',
            objectFit: 'cover',
            color: '#ffffff',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.5)',
            letterSpacing: '1px',
            position: 'relative',
            overflow: 'visible',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              right: '-4px',
              bottom: '-4px',
              background: colorScheme.background,
              borderRadius: '50%',
              opacity: 0.3,
              filter: 'blur(12px)',
              zIndex: -1,
            },
            '@keyframes gentle-pulse': {
              '0%, 100%': {
                transform: 'scale(1)',
                boxShadow: `0 8px 32px ${colorScheme.shadow}, 0 4px 16px ${colorScheme.glow}, 0 0 0 4px rgba(255, 255, 255, 0.25), inset 0 2px 4px rgba(255, 255, 255, 0.3)`,
              },
              '50%': {
                transform: 'scale(1.05)',
                boxShadow: `0 12px 40px ${colorScheme.shadow}, 0 6px 24px ${colorScheme.glow}, 0 0 0 4px rgba(255, 255, 255, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.4)`,
              },
            },
            '@keyframes subtle-rotate': {
              '0%': {
                filter: 'hue-rotate(0deg)',
              },
              '100%': {
                filter: 'hue-rotate(15deg)',
              },
            },
          }}
          imgProps={{
            style: {
              borderRadius: '50%',
              objectFit: 'cover',
            },
            onError: (e) => {
              // If image fails to load, hide src to show initials or person icon
              e.target.style.display = 'none'
            },
          }}
        >
          {!profilePictureUrl && (() => {
            const initials = getInitials()
            // If getInitials returns null, show person icon instead
            if (initials === null) {
              return <PersonIcon sx={{ fontSize: 52, color: 'rgba(255, 255, 255, 0.95)', filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }} />
            }
            // Otherwise show initials
            return initials
          })()}
        </Avatar>
        
        {/* Name and (You) text below avatar - only for local user */}
        {user?.uid === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.25,
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: 'rgba(0, 0, 0, 0.87)',
                textAlign: 'center',
                lineHeight: 1.2,
              }}
            >
              {userInfo.name !== 'Loading...' ? userInfo.name : 'You'}
            </Typography>
            {/* Only show (You) if name is not "You" (i.e., if we have actual name from RTM) */}
            {userInfo.name !== 'You' && userInfo.name !== 'Loading...' && (
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'rgba(0, 0, 0, 0.6)',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                (You)
              </Typography>
            )}
          </Box>
        )}
      </Box>

    </Box>
  )
}

export default CustomVideoPlaceholder

